import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAppConfig } from "../../lib/config";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Loader2, Play, Search, X, Download } from "lucide-react";

import RangeSelector, { RANGE_OPTIONS } from "./rank/RangeSelector";
import VisibilityChart from "./rank/VisibilityChart";
import KeywordTable from "./rank/KeywordTable";
import HistoryList from "./rank/HistoryList";

const hostOf = (u) =>
  (u || "").toLowerCase().replace(/^https?:\/\//, "").split(/[/?#:]/)[0].replace(/^www\./, "");

// True when `url` is on the client's domain (or one of its subdomains).
function isOwnUrl(url, domain) {
  const d = hostOf(domain);
  const h = hostOf(url);
  return !!d && !!h && (h === d || h.endsWith(`.${d}`));
}

/**
 * Rank tracking tab — orchestrates data fetching + state, delegates rendering to
 * `rank/*` subcomponents (range selector, visibility chart, keyword table, history list).
 */
export default function RankTab({ client, reloadClient }) {
  const { max_keywords_per_client: KW_LIMIT } = useAppConfig();
  const { isAdmin } = useAuth();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [removingKw, setRemovingKw] = useState(null);
  const [range, setRange] = useState("all");
  const [query, setQuery] = useState("");
  const [now] = useState(() => Date.now()); // captured once so the memos stay pure
  const [gscByKw, setGscByKw] = useState(null);  // lowercase keyword → {impressions, clicks, ctr, position}
  const isLocal = client?.campaign_type === "local";

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setFetching(true);
      try {
        const { data } = await api.get(`/clients/${client.id}/rank-snapshots`);
        if (alive) setSnapshots(data.snapshots || []);
      } finally {
        if (alive) setFetching(false);
      }
    })();
    // Fetch GSC keyword stats in parallel — non-blocking; if GSC isn't connected
    // or the user has no GSC property, we silently skip the column.
    (async () => {
      try {
        const { data } = await api.get(`/clients/${client.id}/gsc/keyword-stats`);
        if (alive) setGscByKw(data?.configured ? (data.stats || {}) : null);
      } catch {
        if (alive) setGscByKw(null);
      }
    })();
    return () => { alive = false; };
  }, [client.id]);

  // Progress reflects the live job state when running, e.g. { done: 12, total: 30 }
  const [jobProgress, setJobProgress] = useState(null);

  // Shared poll loop — works for both newly-started jobs (from clicking the
  // button) and resumed jobs (detected on page mount). Returns when the job
  // hits a terminal state.
  const pollJob = (jobId) => new Promise((resolve, reject) => {
    let timer = null;
    const tick = async () => {
      try {
        const { data: job } = await api.get(`/rank-jobs/${jobId}`);
        setJobProgress(job.progress || null);
        if (job.status === "completed") return resolve(job);
        if (job.status === "failed") return reject(new Error(job.error || "Rank check failed"));
        timer = setTimeout(tick, 3000);
      } catch (err) {
        if (timer) clearTimeout(timer);
        reject(err);
      }
    };
    tick();
  });

  const reloadSnapshots = async () => {
    const { data: snaps } = await api.get(`/clients/${client.id}/rank-snapshots`);
    setSnapshots(snaps.snapshots || []);
    return snaps.snapshots || [];
  };

  // On mount: check if there's already a job running for this client (e.g. the
  // user closed the tab mid-run). If so, attach to it so the progress counter
  // resumes and the button stays disabled until it finishes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${client.id}/rank-jobs/active`);
        const active = data?.job;
        if (!active || cancelled) return;
        setLoading(true);
        setJobProgress(active.progress || null);
        try {
          await pollJob(active.id);
          if (!cancelled) {
            const all = await reloadSnapshots();
            toast.success(`Rank check finished. ${all[0]?.results?.length || 0} keywords scored.`);
          }
        } catch (e) {
          if (!cancelled) toast.error(e?.message || "Rank check failed");
        } finally {
          if (!cancelled) {
            setJobProgress(null);
            setLoading(false);
          }
        }
      } catch {
        // No active job, or endpoint failed — ignore silently
      }
    })();
    return () => { cancelled = true; };
  }, [client.id]);

  const run = async () => {
    if (!client.keywords?.length) {
      toast.error("Add target keywords to the client first.");
      return;
    }
    if (loading) {
      toast.info("A rank check is already running for this client.");
      return;
    }
    setLoading(true);
    setJobProgress({ done: 0, total: client.keywords.length });
    try {
      const { data: kicked } = await api.post(`/clients/${client.id}/rank-track`);
      await pollJob(kicked.job_id);
      const all = await reloadSnapshots();
      toast.success(`Checked ${all[0]?.results?.length || client.keywords.length} keywords.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Rank check failed");
    } finally {
      setJobProgress(null);
      setLoading(false);
    }
  };

  const removeKeyword = async (kw, e) => {
    e?.stopPropagation();
    if (!kw) return;
    if (!window.confirm(`Stop tracking "${kw}"?`)) return;
    setRemovingKw(kw);
    try {
      const next = (client.keywords || []).filter((k) => k.toLowerCase() !== kw.toLowerCase());
      await api.patch(`/clients/${client.id}`, { keywords: next });
      toast.success(`Removed "${kw}".`);
      reloadClient?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    } finally {
      setRemovingKw(null);
    }
  };

  const latest = snapshots[0];
  const prevRun = snapshots[1];
  const latestResults = latest?.results || [];
  // Merge `client.keywords` with the latest snapshot's results so newly added
  // keywords appear immediately with a "pending first crawl" badge instead of
  // staying invisible until the next scheduled rank check.
  const list = useMemo(() => {
    const ownOnly = (r) => (r && r.url && !isOwnUrl(r.url, client?.domain) ? { ...r, url: "" } : r);
    const cleaned = latestResults.map(ownOnly);
    const byKw = new Map();
    for (const r of cleaned) {
      if (r?.keyword) byKw.set(r.keyword, r);
    }
    // Prefer the new split-scope lists; fall back to the flat legacy list
    // for clients that haven't hit the startup migration yet.
    const nat = client?.national_keywords || [];
    const loc = client?.local_keywords || [];
    const trackedKws =
      (nat.length || loc.length)
        ? [...nat.map((k) => ({ kw: k, scope: "national" })),
           ...loc.map((k) => ({ kw: k, scope: "local" }))]
        : (client?.keywords || []).map((k) => ({ kw: k, scope: null }));
    if (trackedKws.length === 0) return cleaned;
    const merged = [];
    // Key by (lowercase keyword, scope) so a keyword tracked at BOTH scopes
    // appears twice — once per scope — matching how the backend polls it.
    const seen = new Set();
    for (const { kw, scope } of trackedKws) {
      const dedupeKey = `${kw.toLowerCase()}::${scope || "any"}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      // Match on both keyword + scope where possible; fall back on
      // keyword-only for legacy snapshots that don't carry a scope tag.
      const row =
        cleaned.find(
          (r) => r?.keyword === kw && (scope == null || r.scope === scope || r.scope == null),
        ) || byKw.get(kw);
      merged.push(
        row
          ? { ...row, scope: row.scope || scope }
          : { keyword: kw, scope, position: null, search_volume: null, url: "", pending: true },
      );
    }
    return merged;
  }, [latestResults, client?.domain, client?.keywords, client?.national_keywords, client?.local_keywords]);
  const getPrev = (kw) => prevRun?.results?.find((p) => p.keyword === kw)?.position;

  // Scope filter: "all" | "national" | "local". Legacy rows without a
  // `scope` tag are treated as belonging to whichever bucket the current
  // client campaign_type implies, so pre-migration data still shows up in
  // the National or Local tabs sensibly.
  const [scopeFilter, setScopeFilter] = useState("all");
  const scopeCounts = useMemo(() => {
    const c = { all: list.length, national: 0, local: 0 };
    for (const r of list) {
      const s = r.scope || (client?.campaign_type === "local" ? "local" : "national");
      if (s === "national") c.national += 1;
      else if (s === "local") c.local += 1;
    }
    return c;
  }, [list, client?.campaign_type]);

  // Case-insensitive substring filter over the latest snapshot's keywords,
  // then narrowed by the active scope tab.
  const trimmedQuery = query.trim().toLowerCase();
  const filteredList = useMemo(() => {
    let out = list;
    if (scopeFilter !== "all") {
      out = out.filter((r) => {
        const s = r.scope || (client?.campaign_type === "local" ? "local" : "national");
        return s === scopeFilter;
      });
    }
    if (trimmedQuery) {
      out = out.filter((r) => (r.keyword || "").toLowerCase().includes(trimmedQuery));
    }
    return out;
  }, [list, trimmedQuery, scopeFilter, client?.campaign_type]);

  // Range slice (chronologically newest first in `snapshots`).
  const rangedSnaps = useMemo(() => {
    const opt = RANGE_OPTIONS.find((o) => o.v === range);
    if (!opt?.days) return snapshots;
    const cutoff = now - opt.days * 24 * 60 * 60 * 1000;
    return snapshots.filter((s) => new Date(s.at).getTime() >= cutoff);
  }, [snapshots, range, now]);

  // Baseline = OLDEST snapshot within the range → deltas show "how rankings changed in the period".
  const baselineSnap = rangedSnaps[rangedSnaps.length - 1];
  const baselinePosByKw = useMemo(() => {
    const m = new Map();
    for (const r of (baselineSnap?.results || [])) m.set(r.keyword, r.position);
    return m;
  }, [baselineSnap]);
  const baselineLabel = useMemo(() => {
    if (!baselineSnap) return null;
    return new Date(baselineSnap.at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }, [baselineSnap]);

  // Visibility series — % of tracked keywords in Top 10 / Top 3 per snapshot (oldest → newest for chart).
  const visibilitySeries = useMemo(() => {
    return [...rangedSnaps].reverse().map((s) => {
      const results = s.results || [];
      const total = results.length || 1;
      const top10 = results.filter((r) => r.position != null && r.position <= 10).length;
      const top3 = results.filter((r) => r.position != null && r.position <= 3).length;
      return {
        at: s.at,
        label: new Date(s.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        top10_pct: Math.round((100 * top10) / total),
        top3_pct: Math.round((100 * top3) / total),
        top10,
        top3,
        total: results.length,
      };
    });
  }, [rangedSnaps]);

  /**
   * Export the currently-visible ranking rows to a CSV file. Uses the filtered
   * list (respects the scope filter + search box) so "what you see is what
   * you get". Fields are quoted, RFC-4180 style. Filename encodes the client
   * name and today's date so exports don't overwrite each other.
   */
  const exportCsv = () => {
    const HEADERS = [
      "Keyword",
      "Scope",
      "Position",
      "Local Pack",
      "Change vs previous",
      "Change vs baseline",
      "Search volume",
      "URL",
      "GSC clicks (28d)",
      "GSC impressions (28d)",
      "GSC CTR (28d)",
      "GSC avg position (28d)",
    ];
    const escape = (v) => {
      if (v == null || v === "") return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filteredList.map((r) => {
      const prev = getPrev?.(r.keyword);
      const delta = prev != null && r.position != null ? prev - r.position : null;
      const base = baselinePosByKw?.get?.(r.keyword);
      const baseDelta = base != null && r.position != null ? base - r.position : null;
      const gsc = gscByKw?.[(r.keyword || "").toLowerCase()] || {};
      return [
        r.keyword,
        r.scope || (isLocal ? "local" : "national"),
        r.position ?? "",
        r.local_pack_position ?? "",
        delta ?? "",
        baseDelta ?? "",
        r.search_volume ?? "",
        r.url ?? "",
        gsc.clicks ?? "",
        gsc.impressions ?? "",
        gsc.ctr != null ? `${(gsc.ctr * 100).toFixed(2)}%` : "",
        gsc.position != null ? gsc.position.toFixed(1) : "",
      ].map(escape).join(",");
    });
    const csv = [HEADERS.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = (client?.name || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "client";
    const today = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `${safeName}_rankings_${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredList.length} keyword${filteredList.length === 1 ? "" : "s"} to CSV`);
  };

  return (
    <div className="space-y-4" data-testid="rank-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Rank tracking</div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Position monitor</h3>
          <p className="text-xs text-[#718096] mt-1">
            Google SERP checks via DataForSEO's standard queue (exact ranking pages · usually 2–5 min per check) · Snapshots stored on the server · Rankings checked weekly · Mon 09:00 UTC.
          </p>
          <p
            className="text-[10px] text-[#a0aec0] mt-1 font-mono-tight"
            data-testid="rank-keyword-limit-note"
          >
            Tracking {(client?.keywords || []).length} / {KW_LIMIT} keywords (server-side cap).
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={run}
            disabled={loading || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="rank-run"
            className="btn-primary inline-flex items-center gap-2"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {loading
              ? jobProgress
                ? `Checking ${jobProgress.done}/${jobProgress.total}…`
                : "Checking…"
              : "Check ranks now"}
          </button>
        )}
      </div>

      <RangeSelector
        value={range}
        onChange={setRange}
        baselineLabel={baselineLabel}
        snapshotCount={rangedSnaps.length}
      />

      <VisibilityChart series={visibilitySeries} />

      {(scopeCounts.national > 0 && scopeCounts.local > 0) && (
        <div
          className="flex items-center gap-1 rounded-xl border border-[#e2e8f0] bg-[#f7fafc] p-1 self-start"
          role="tablist"
          aria-label="Rank tracking scope filter"
          data-testid="rank-scope-filter"
        >
          {[
            { key: "all", label: `All (${scopeCounts.all})` },
            { key: "national", label: `National (${scopeCounts.national})` },
            { key: "local", label: `Local (${scopeCounts.local})` },
          ].map((opt) => {
            const active = scopeFilter === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`rank-scope-${opt.key}`}
                onClick={() => setScopeFilter(opt.key)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-all ${
                  active
                    ? "bg-white text-[#234e52] shadow-[0_2px_4px_-1px_rgba(45,55,72,0.08)] border border-[#e2e8f0]"
                    : "text-[#718096] hover:text-[#234e52]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap" data-testid="rank-search-bar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a0aec0] pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tracked keywords…"
            aria-label="Search tracked keywords"
            data-testid="rank-search-input"
            className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-[#edf2f7] bg-white text-[#1a202c] placeholder:text-[#a0aec0] focus:outline-none focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/20 transition-colors"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="rank-search-clear"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full inline-flex items-center justify-center text-[#a0aec0] hover:text-[#4a5568] hover:bg-[#edf2f7] transition-colors"
            >
              <X className="h-3 w-3" strokeWidth={2.4} />
            </button>
          )}
        </div>
        {list.length > 0 && (
          <div className="flex items-center gap-3">
            <div
              className="text-xs font-mono-tight text-[#a0aec0]"
              data-testid="rank-search-count"
            >
              {trimmedQuery ? (
                <span>
                  <span className="text-[#1a202c] font-semibold">{filteredList.length}</span>
                  <span> of {list.length} keywords</span>
                </span>
              ) : (
                <span>{list.length} keywords tracked</span>
              )}
            </div>
            <button
              type="button"
              onClick={exportCsv}
              disabled={filteredList.length === 0}
              data-testid="rank-export-csv"
              title="Export the currently-visible rows to a CSV file"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white hover:bg-[#f7fafc] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[#4a5568] hover:text-[#1a202c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="h-3 w-3" strokeWidth={2.4} />
              Export CSV
            </button>
          </div>
        )}
      </div>

      <KeywordTable
        list={filteredList}
        isLocal={isLocal}
        fetching={fetching}
        baselineLabel={baselineLabel}
        baselinePosByKw={baselinePosByKw}
        getPrev={getPrev}
        onRemoveKeyword={removeKeyword}
        removingKw={removingKw}
        gscByKw={gscByKw}
        latestSnapshotAt={latest?.at}
        latestSerpLocations={latest?.serp_locations || latest?.serp_location}
        emptyMessage={
          trimmedQuery
            ? `No tracked keywords match "${query}".`
            : undefined
        }
      />

      <HistoryList snapshots={snapshots} />
    </div>
  );
}
